import { useState } from "react";
import {
  FiUser,
  FiMail,
  FiLock,
  FiMapPin,
  FiEye,
  FiEyeOff,
  FiCheckCircle,
  FiAlertTriangle,
  FiPlus,
  FiMinus,
} from "react-icons/fi";
import { FaHotel } from "react-icons/fa";
import axios from "axios";
import { getBaseUrl } from "../../Redux/Config/AppConfig";
import { Trash } from "lucide-react";

const CreateHotelAdmin = () => {
  const [form, setForm] = useState({
    hotelName: "",
    hotelAddress: "",
    adminFirstName: "",
    adminLastName: "",
    adminEmail: "",
    adminPassword: "",
  });

  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [hotels, setHotels] = useState([{ hotelName: "", hotelAddress: "" }]);
  const [hotelErrors, setHotelErrors] = useState([]);
  const handleHotelChange = (idx, e) => {
    const { name, value } = e.target;
    setHotels((prev) =>
      prev.map((hotel, i) => (i === idx ? { ...hotel, [name]: value } : hotel))
    );

    // Inline validation for hotel name
    if (name === "hotelName") {
      setHotelErrors((prev) => {
        const newErrors = [...prev];
        newErrors[idx] = {
          ...newErrors[idx],
          hotelName: value.trim() ? "" : "Hotel name is required",
        };
        return newErrors;
      });
    }
  };

  const addHotel = () =>
    setHotels([...hotels, { hotelName: "", hotelAddress: "" }]);
  const removeHotel = (idx) => setHotels(hotels.filter((_, i) => i !== idx));

  const validateField = (name, value) => {
    switch (name) {
      // case "hotelName":
      //   return value.trim() ? "" : "Hotel name is required";
      case "adminFirstName":
        return value.trim() ? "" : "First name is required";
      case "adminLastName":
        return value.trim() ? "" : "Last name is required";
      case "adminEmail":
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
          ? ""
          : "Valid email is required";
      case "adminPassword":
        return value.length >= 8
          ? ""
          : "Password must be at least 8 characters";
      default:
        return "";
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });

    // Real-time validation
    const error = validateField(name, value);
    setErrors({ ...errors, [name]: error });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccess("");
    setError("");

    // Validate all fields
    const newErrors = {};
    Object.keys(form).forEach((key) => {
      if (key !== "hotelAddress") {
        const error = validateField(key, form[key]);
        if (error) newErrors[key] = error;
      }
    });

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0 || hotelErrors.some((item)=>item.hotelName!=='')) return;

    setIsLoading(true);

    try {
      const baseUrl = getBaseUrl();
      const res = await axios.post(`${baseUrl}/master/users`, {
        first_name: form.adminFirstName,
        last_name: form.adminLastName,
        email: form.adminEmail,
        password: form.adminPassword,
        // hotel_name: form.hotelName,
        // hotel_address: form.hotelAddress,
        hotels,
        phone: "1112223334",
      });

      setSuccess(
        `Hotel admin created successfully! Database: ${res.data.data.database_name}`
      );
      setForm({
        hotelName: "",
        hotelAddress: "",
        adminFirstName: "",
        adminLastName: "",
        adminEmail: "",
        adminPassword: "",
      });
      setHotels([{ hotelName: "", hotelAddress: "" }])
    } catch (err) {
      setError(err.response?.data?.message || "Failed to create hotel admin");
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div
      className="min-vh-100 d-flex align-items-center justify-content-center"
      style={{
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        padding: "1rem",
      }}
    >
      <div
        className="card shadow-lg"
        style={{
          maxWidth: 750,
          width: "100%",
          borderRadius: "1rem",
          padding: "2rem",
          backgroundColor: "rgba(255, 255, 255, 0.95)",
        }}
      >
        <h2
          className="card-title text-center mb-4 fw-bold"
          style={{ color: "#333" }}
        >
          Create Hotel Admin
        </h2>
        <p className="text-center text-muted mb-4">
          Set up a new hotel and administrator account
        </p>

        <form onSubmit={handleSubmit}>
          {/* Hotel Information Section */}
          <div className="mb-4">
            <h5
              className="fw-semibold mb-3 text-center"
              style={{ color: "#333" }}
            >
              Hotel Information
            </h5>
            {hotels.map((hotel, idx) => (
              <div className="row mb-3" key={idx}>
                <div className="col-md-5">
                  <div className="mb-2 position-relative">
                    <label className="form-label fw-semibold">
                      Hotel Name <span className="text-danger">*</span>
                    </label>
                    <div className="input-group">
                      <span
                        className="input-group-text bg-white border-end-0"
                        style={{ borderRadius: "0.375rem 0 0 0.375rem" }}
                      >
                        <FaHotel size={18} color="#555" />
                      </span>
                      <input
                        type="text"
                        name="hotelName"
                        value={hotel.hotelName}
                        onChange={(e) => handleHotelChange(idx, e)}
                        className="form-control border-start-0"
                        placeholder="Enter hotel name"
                        required
                        style={{ borderRadius: "0 0.375rem 0.375rem 0" }}
                      />
                    </div>
                  </div>
                </div>
                <div className="col-md-5">
                  <div className="mb-2 position-relative">
                    <label className="form-label fw-semibold">
                      Hotel Address
                    </label>
                    <div className="input-group">
                      <span
                        className="input-group-text bg-white border-end-0"
                        style={{ borderRadius: "0.375rem 0 0 0.375rem" }}
                      >
                        <FiMapPin size={18} color="#555" />
                      </span>
                      <input
                        type="text"
                        name="hotelAddress"
                        value={hotel.hotelAddress}
                        onChange={(e) => handleHotelChange(idx, e)}
                        className="form-control border-start-0"
                        placeholder="Enter hotel address (optional)"
                        style={{ borderRadius: "0 0.375rem 0.375rem 0" }}
                      />
                    </div>
                  </div>
                </div>
                <div className="col-md-2" style={{ paddingTop: "30px" }}>
                  <button
                    className="btn"
                    title="Remove hotel"
                    onClick={() => removeHotel(idx)}
                  >
                    <i className="bi bi-trash text-danger"></i>
                  </button>
                  {/* <button
                    type="button"
                    className="btn btn-outline btn-danger"
                    title="Remove hotel"
                    onClick={() => removeHotel(idx)}
                  >
                    <Trash />
                  </button> */}
                </div>
                {/* Inline validation error for hotel name */}
                {hotelErrors[idx]?.hotelName && (
                  <div className="text-danger small">
                    {hotelErrors[idx].hotelName}
                  </div>
                )}
              </div>
            ))}
            <div className="text-end">
              <button
                type="button"
                className="btn btn-success"
                title="Add hotel"
                onClick={addHotel}
              >
                {/* <FiPlus /> */}
                Add Hotel
              </button>
            </div>
          </div>

          {/* Administrator Details Section */}
          <div className="mb-4">
            <h5
              className="fw-semibold mb-3 text-center"
              style={{ color: "#333" }}
            >
              Administrator Details
            </h5>

            <div className="row mb-4">
              <div className="col-md-6">
                <label className="form-label fw-semibold">
                  First Name <span className="text-danger">*</span>
                </label>
                <div className="input-group">
                  <span
                    className="input-group-text bg-white border-end-0"
                    style={{ borderRadius: "0.375rem 0 0 0.375rem" }}
                  >
                    <FiUser size={18} color="#555" />
                  </span>
                  <input
                    type="text"
                    name="adminFirstName"
                    value={form.adminFirstName}
                    onChange={handleChange}
                    className={`form-control border-start-0 ${
                      errors.adminFirstName ? "is-invalid" : ""
                    }`}
                    placeholder="First name"
                    required
                    style={{ borderRadius: "0 0.375rem 0.375rem 0" }}
                  />
                </div>
                {errors.adminFirstName && (
                  <div className="invalid-feedback d-block">
                    {errors.adminFirstName}
                  </div>
                )}
              </div>

              <div className="col-md-6">
                <label className="form-label fw-semibold">
                  Last Name <span className="text-danger">*</span>
                </label>
                <div className="input-group">
                  <span
                    className="input-group-text bg-white border-end-0"
                    style={{ borderRadius: "0.375rem 0 0 0.375rem" }}
                  >
                    <FiUser size={18} color="#555" />
                  </span>
                  <input
                    type="text"
                    name="adminLastName"
                    value={form.adminLastName}
                    onChange={handleChange}
                    className={`form-control border-start-0 ${
                      errors.adminLastName ? "is-invalid" : ""
                    }`}
                    placeholder="Last name"
                    required
                    style={{ borderRadius: "0 0.375rem 0.375rem 0" }}
                  />
                </div>
                {errors.adminLastName && (
                  <div className="invalid-feedback d-block">
                    {errors.adminLastName}
                  </div>
                )}
              </div>
            </div>

            <div className="mb-4 position-relative">
              <label className="form-label fw-semibold">
                Email Address <span className="text-danger">*</span>
              </label>
              <div className="input-group">
                <span
                  className="input-group-text bg-white border-end-0"
                  style={{ borderRadius: "0.375rem 0 0 0.375rem" }}
                >
                  <FiMail size={18} color="#555" />
                </span>
                <input
                  type="email"
                  name="adminEmail"
                  value={form.adminEmail}
                  onChange={handleChange}
                  className={`form-control border-start-0 ${
                    errors.adminEmail ? "is-invalid" : ""
                  }`}
                  placeholder="admin@hotel.com"
                  autoComplete="off"
                  required
                  style={{ borderRadius: "0 0.375rem 0.375rem 0" }}
                />
              </div>
              {errors.adminEmail && (
                <div className="invalid-feedback d-block">
                  {errors.adminEmail}
                </div>
              )}
            </div>

            <div className="mb-4 position-relative">
              <label className="form-label fw-semibold">
                Password <span className="text-danger">*</span>
              </label>
              <div className="input-group">
                <span
                  className="input-group-text bg-white border-end-0"
                  style={{ borderRadius: "0.375rem 0 0 0.375rem" }}
                >
                  <FiLock size={18} color="#555" />
                </span>
                <input
                  type={showPassword ? "text" : "password"}
                  name="adminPassword"
                  value={form.adminPassword}
                  onChange={handleChange}
                  className={`form-control border-start-0 ${
                    errors.adminPassword ? "is-invalid" : ""
                  }`}
                  placeholder="Minimum 8 characters"
                  required
                  autoComplete="off"
                  style={{ borderRadius: "0 0.375rem 0.375rem 0" }}
                />
                <button
                  type="button"
                  onClick={togglePasswordVisibility}
                  className="btn btn-outline-secondary border-start-0"
                  style={{
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                    borderTopRightRadius: "0.375rem",
                    borderBottomRightRadius: "0.375rem",
                    padding: "0.375rem 0.5rem",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  tabIndex={-1}
                >
                  {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                </button>
              </div>
              {errors.adminPassword && (
                <div className="invalid-feedback d-block">
                  {errors.adminPassword}
                </div>
              )}
            </div>
          </div>

          <button
            type="submit"
            className="btn w-100 py-2 fw-bold"
            disabled={isLoading}
            style={{
              fontSize: "1.1rem",
              borderRadius: "0.75rem",
              letterSpacing: "0.05em",
              backgroundColor: "#000",
              color: "#fff",
              boxShadow: "0 4px 15px rgba(0, 0, 0, 0.4)",
              transition: "background-color 0.3s ease",
              border: "none",
            }}
          >
            {isLoading ? "Creating Admin..." : "Create Hotel Admin"}
          </button>
        </form>

        {/* Success Message */}
        {success && (
          <div
            className="mt-4 alert alert-success d-flex align-items-center"
            role="alert"
          >
            <FiCheckCircle className="me-2" />
            <div>{success}</div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div
            className="mt-4 alert alert-danger d-flex align-items-center"
            role="alert"
          >
            <FiAlertTriangle className="me-2" />
            <div>{error}</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateHotelAdmin;
